import type { Building, EconomyState, GridCell, GridCoord, ZoneType } from '../../types/core';
import { cellKey, isInside, neighbours, rectCoords } from '../../utils/grid';
import { createBuilding } from './buildings';
import { roadsAroundBlock, withRoads } from './roads';

export interface ZonePlacementResult {
  readonly cells: readonly GridCell[];
  readonly buildings: readonly Building[];
  readonly economy: EconomyState;
  readonly placedCount: number;
  readonly spentCredits: number;
}

const zoneCosts: Record<ZoneType, number> = {
  residential: 180,
  commercial: 260,
  workspace: 340,
  park: 90
};

export function costForZone(zone: ZoneType): number {
  return zoneCosts[zone];
}

function connectPerimeterToNetwork(
  existingCells: readonly GridCell[],
  perimeterRoads: readonly GridCoord[],
  size: number
): GridCoord[] {
  const existingRoads = existingCells.filter((cell) => cell.road && !cell.buildingId).map((cell) => cell.coord);
  if (existingRoads.length === 0 || perimeterRoads.length === 0) {
    return [];
  }

  let bestStart = perimeterRoads[0];
  let bestEnd = existingRoads[0];
  let bestDistance = Number.POSITIVE_INFINITY;

  for (const start of perimeterRoads) {
    for (const end of existingRoads) {
      const distance = Math.abs(start.x - end.x) + Math.abs(start.z - end.z);
      if (distance < bestDistance) {
        bestDistance = distance;
        bestStart = start;
        bestEnd = end;
      }
    }
  }

  const connector: GridCoord[] = [];
  let cursor: GridCoord = { ...bestStart };
  const stepX = Math.sign(bestEnd.x - cursor.x);

  while (cursor.x !== bestEnd.x) {
    cursor = { x: cursor.x + stepX, z: cursor.z };
    if (isInside(cursor, size)) {
      connector.push(cursor);
    }
  }

  const stepZ = Math.sign(bestEnd.z - cursor.z);

  while (cursor.z !== bestEnd.z) {
    cursor = { x: cursor.x, z: cursor.z + stepZ };
    if (isInside(cursor, size)) {
      connector.push(cursor);
    }
  }

  const blocked = new Set(
    existingCells
      .filter((cell) => cell.buildingId)
      .map((cell) => cell.key)
  );

  return connector.filter((coord) => !blocked.has(cellKey(coord)));
}

export function placeZoneBlock(
  cells: readonly GridCell[],
  buildings: readonly Building[],
  economy: EconomyState,
  zone: ZoneType,
  start: GridCoord,
  end: GridCoord,
  size: number,
  day: number,
  rng: () => number
): ZonePlacementResult {
  const selected = rectCoords(start, end).filter((coord) => isInside(coord, size));
  const open = selected.filter((coord) => {
    const key = cellKey(coord);
    const cell = cells.find((item) => item.key === key);
    return Boolean(cell && !cell.buildingId);
  });

  if (open.length === 0) {
    return { cells, buildings, economy, placedCount: 0, spentCredits: 0 };
  }

  const maxAffordable = Math.floor(economy.credits / zoneCosts[zone]);
  const buildable = open.slice(0, Math.max(0, maxAffordable));

  if (buildable.length === 0) {
    return { cells, buildings, economy, placedCount: 0, spentCredits: 0 };
  }

  const blockKeys = new Set(buildable.map(cellKey));
  const newBuildings = buildable.map((coord, index) => createBuilding(zone, [coord], day, rng, buildings.length + index));
  const buildingByCell = new Map(newBuildings.flatMap((building) => building.cells.map((coord) => [cellKey(coord), building.id])));
  const perimeterRoads = roadsAroundBlock(buildable, size);
  const connectorRoads = connectPerimeterToNetwork(cells, perimeterRoads, size);
  const roadCoords = [...perimeterRoads, ...connectorRoads];
  const cellsWithoutInteriorRoads = cells.map((cell) => {
    if (!blockKeys.has(cell.key)) {
      return cell;
    }

    return {
      ...cell,
      road: false,
      roadEdge: false,
      zone,
      buildingId: buildingByCell.get(cell.key)
    };
  });
  const updatedCells = withRoads(cellsWithoutInteriorRoads, roadCoords, size);
  const spentCredits = buildable.length * zoneCosts[zone];

  return {
    cells: updatedCells,
    buildings: [...buildings, ...newBuildings],
    economy: {
      ...economy,
      credits: economy.credits - spentCredits
    },
    placedCount: buildable.length,
    spentCredits
  };
}

export function selectableNeighbours(coord: GridCoord, cells: readonly GridCell[], size: number): GridCoord[] {
  const cellByKey = new Map(cells.map((cell) => [cell.key, cell]));

  return neighbours(coord).filter((candidate) => {
    const cell = cellByKey.get(cellKey(candidate));
    return isInside(candidate, size) && Boolean(cell && !cell.buildingId);
  });
}
